import {
  Megaphone,
  Sparkles,
  TrendingUp,
  BadgePercent,
  BellRing,
  Boxes,
  ClipboardList,
  BrainCircuit,
  LayoutDashboard,
  MessagesSquare,
  Package,
  Plug,
  Settings,
  ShoppingCart,
  Tags,
  Truck,
  Wallet,
  Workflow,
} from 'lucide-react';
import { AppLayout } from '@/components/shell/app-layout';
import { visibleNav, type NavItem } from '@/components/shell/navigation';
import { useAuth } from '@/features/auth/use-auth';
import { AssistantSheet } from '@/features/intelligence/components/assistant-sheet';
import { OrganizationSwitcher } from './organization-switcher';

/**
 * Módulos do sistema da empresa, na ordem do §19 do `docs/TROIG_ESCOPO_COMPLETO.md`. Os ainda
 * não construídos aparecem como "em breve" (decisão do dono): mostram para onde o produto vai.
 * Ao construir um, troque `soon` por `link` — a ordem já está certa.
 */
const COMPANY_NAV: NavItem[] = [
  {
    kind: 'link',
    label: 'Visão geral',
    to: '/',
    icon: LayoutDashboard,
    permission: 'catalog.read',
  },
  { kind: 'link', label: 'Catálogo', to: '/produtos', icon: Package, permission: 'catalog.read' },
  { kind: 'link', label: 'Anúncios', to: '/anuncios', icon: Tags, permission: 'catalog.read' },
  {
    kind: 'link',
    label: 'Promoções',
    to: '/promocoes',
    icon: BadgePercent,
    permission: 'financials.read',
  },
  {
    kind: 'group',
    label: 'Pedidos',
    icon: ShoppingCart,
    children: [
      { label: 'Em aberto', to: '/pedidos', permission: 'catalog.read' },
      { label: 'Consultar', to: '/pedidos/consulta', permission: 'catalog.read' },
      { label: 'Reputação', to: '/reputacao', permission: 'catalog.read' },
    ],
  },
  { kind: 'link', label: 'Estoque', to: '/estoque', icon: Boxes, permission: 'catalog.read' },
  {
    kind: 'link',
    label: 'Compras',
    to: '/compras',
    icon: ClipboardList,
    permission: 'catalog.read',
  },
  {
    kind: 'group',
    label: 'Financeiro',
    icon: Wallet,
    children: [
      { label: 'Resultado', to: '/financeiro', permission: 'finance.read' },
      { label: 'A receber', to: '/financeiro/a-receber', permission: 'finance.read' },
      { label: 'Caixa previsto', to: '/financeiro/caixa', permission: 'finance.read' },
      { label: 'Despesas fixas', to: '/financeiro/despesas', permission: 'finance.read' },
    ],
  },
  {
    kind: 'group',
    label: 'Atendimento',
    icon: MessagesSquare,
    children: [
      { label: 'Perguntas', to: '/atendimento/perguntas', permission: 'support.read' },
      { label: 'Pós-venda', to: '/atendimento/pos-venda', permission: 'support.read' },
    ],
  },
  {
    kind: 'soon',
    label: 'Logística',
    icon: Truck,
    description: 'Envios, prazos e frete realizado.',
  },
  { kind: 'link', label: 'Alertas', to: '/acoes', icon: BellRing, permission: 'catalog.read' },
  {
    kind: 'link',
    label: 'Inteligência',
    to: '/inteligencia',
    icon: BrainCircuit,
    permission: 'catalog.read',
  },
  {
    kind: 'link',
    label: 'Oportunidades',
    to: '/oportunidades',
    icon: Sparkles,
    permission: 'catalog.read',
  },
  {
    kind: 'link',
    label: 'Mercado Ads',
    to: '/mercado-ads',
    icon: Megaphone,
    permission: 'catalog.read',
  },
  {
    kind: 'link',
    label: 'Resultados',
    to: '/resultados',
    icon: TrendingUp,
    permission: 'financials.read',
  },
  {
    kind: 'link',
    label: 'Automações',
    to: '/automacoes',
    icon: Workflow,
    permission: 'catalog.read',
  },
  {
    kind: 'link',
    label: 'Integrações',
    to: '/integracoes',
    icon: Plug,
    permission: 'catalog.read',
  },
  {
    kind: 'group',
    label: 'Configurações',
    icon: Settings,
    children: [
      { label: 'Conta completa', to: '/empresa/conta-completa', permission: 'catalog.read' },
      { label: 'Custos e metas', to: '/empresa/custos', permission: 'financials.read' },
      { label: 'Perfil fiscal', to: '/empresa/fiscal', permission: 'revenue.read' },
      {
        label: 'Notas fiscais (SEFAZ)',
        to: '/empresa/notas-fiscais',
        permission: 'organization.manage',
      },
      { label: 'Health Score', to: '/empresa/health-score', permission: 'catalog.read' },
      { label: 'Atendimento', to: '/empresa/atendimento', permission: 'organization.manage' },
      { label: 'Membros', to: '/empresa/membros', permission: 'organization.manage' },
      { label: 'Estado do sistema', to: '/sistema' },
    ],
  },
];

/** Moldura das telas da empresa: módulos por permissão e a empresa ativa. */
export function AppShell() {
  const { can } = useAuth();
  return (
    <AppLayout
      home="/"
      items={visibleNav(COMPANY_NAV, can)}
      context={<OrganizationSwitcher />}
      accountPath="/conta"
      headerActions={can('catalog.read') ? <AssistantSheet /> : undefined}
    />
  );
}
